import { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import toast, { Toaster } from "react-hot-toast";
import "./App.css";
import AppRoutes from "./routes/AppRoutes";
import { onMessageListener } from "./firebase";

function App() {
  const notification = useSelector((state) => state?.store?.notification);
  const role = useSelector((state) => state?.auth?.role);
  const [permissionBlocked, setPermissionBlocked] = useState(
    typeof Notification !== "undefined" && Notification.permission === "denied"
  );

  useEffect(() => {
    if (typeof Notification === "undefined") return;

    // Poll for permission changes (helpful if user changes it in settings)
    const interval = setInterval(() => {
      setPermissionBlocked(Notification.permission === "denied");
    }, 5000);
    return () => clearInterval(interval);
  }, []);


  useEffect(() => {
    // Set up Firebase messaging listener
    onMessageListener()
      .then((payload) => {
        console.log("Initial FCM Listener Ready");
      })
      .catch((err) => {
        console.warn("FCM Listener error:", err);
      });
  }, []);

  const alarmIntervalRef = useRef(null);

  // Function to stop the alarm ringer
  const silenceAlarm = () => {
    if (alarmIntervalRef.current) {
      clearTimeout(alarmIntervalRef.current);
      alarmIntervalRef.current = null;
    }
  };

  // Handle Audio and Toast when notification state changes in Redux
  useEffect(() => {
    if (notification && notification.receivedAt) {
      // First, silence any existing alarm to prevent overlaps
      silenceAlarm();

      const orderMatch = notification.body?.match(/Order with ID (\w+)/);
      const orderId = orderMatch ? orderMatch[1] : "";
      
      toast.custom((t) => (
        <div className={`${t.visible ? 'animate-enter' : 'animate-leave'} max-w-md w-full bg-white shadow-2xl rounded-2xl pointer-events-auto flex ring-1 ring-black ring-opacity-5 border-l-8 border-green-600`}>
          <div className="flex-1 w-0 p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0 pt-0.5 text-3xl">
                🔔
              </div>
              <div className="ml-3 flex-1">
                <p className="text-xl font-bold text-gray-900">
                  {notification.title || "New Order!"}
                </p>
                <p className="mt-1 text-lg text-gray-600">
                  {notification.body || "A new order has been received."}
                </p>
                {orderId && (
                  <p className="mt-2 text-sm font-semibold text-green-700">
                    ID: {orderId}
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="flex border-l border-gray-200">
            <button
              onClick={() => {
                silenceAlarm();
                toast.dismiss(t.id);
              }}
              className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-red-600 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-red-500 bg-red-50"
            >
              Close & Silence
            </button>
          </div>
        </div>
      ), { duration: 20000 }); // Longer duration for the toast relative to the sound

      // 2. Play Loud Ringer (Local Urgent sound)
      const audio = new Audio("/order-alert.mp3");
      audio.volume = 1.0;
      
      const playSound = () => {
        audio.currentTime = 0; // Reset sound to start
        audio.play().catch((err) => {
          console.warn("Audio play blocked. Click anywhere on the page to enable sound alerts.", err);
        });
      };
      
      playSound();
      // Repeat sound exactly once more after 10 seconds (for your 9s file, total ~18s)
      const timeout = setTimeout(playSound, 10000);
      alarmIntervalRef.current = timeout;

      // Auto-clear after 20 seconds
      setTimeout(() => {
        if (alarmIntervalRef.current === timeout) {
          silenceAlarm();
        }
      }, 20000);

    }
  }, [notification]);

  return (
    <>
      <Toaster
        position="top-right"
        containerStyle={{ zIndex: 99999 }}
        toastOptions={{
          style: {
            background: "#108e10",
            color: "#FFFFFF",
            padding: "20px !important",
            fontSize: "16px",
            fontWeight: "bold",
            borderRadius: "12px",
          },
        }}
      />
      {((typeof Notification !== "undefined" && (permissionBlocked || Notification.permission !== 'granted')) && role === 'store') && (
        <div className="fixed top-0 left-0 right-0 bg-red-600 text-white p-2.5 text-center z-[9999] font-bold text-sm shadow-lg backdrop-blur-sm bg-red-600/90">
          ⚠️ {Notification.permission === 'denied' ? 'Browser notification permission is BLOCKED.' : 'Notifications are NOT enabled.'} You will not receive audio or popup alerts for new orders. 
          <button onClick={() => Notification.requestPermission().then(() => window.location.reload())} className="ml-4 underline bg-white text-red-600 px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider hover:bg-gray-100 transition-colors">Enable Alerts</button>
        </div>
      )}
      <AppRoutes />
    </>
  );
}



export default App;
